/**
 * Upgrades — пуьстая (purchase, спецификация) механика.
 *
 * Source of truth: `docs/14_upgrades_system.md`.
 *
 * Каждый апгрейд имеет:
 *   - стабильный `id` (никогда не переименовывать — он в localStorage)
 *   - цену в `awarenesses` (малый) или `spirit_balls` (большой)
 *   - экспоненциальный рост цены `cost(N) = baseCost × growthRate^N`
 *   - effect (multiplier / flat / unlock / one-shot)
 *   - **visibleChange** — обязательное поле, отражает Принцип #9
 *     («каждый апгрейд = видимое изменение мира»). CSS-переменная или
 *     componentFlag, который рендереры Искры и сцены подхватывают.
 *
 * Здесь только pure-functions: расчёт цены, проверка affordability,
 * композиция множителя из всех купленных апгрейдов, попытка покупки.
 * UI-сторона живёт в `apps/web/src/components/UpgradesPanel.tsx`.
 *
 * Артефакты из `visual-artifacts/catalog.ts` могут опираться на
 * триггер `upgradePurchasedExactly` чтобы выдавать reveal на каждый
 * новый купленный уровень — это связывает doc 14 с doc 21.
 */

import type { Epoch, PhraseLayer, ResourceState } from './types.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Группа для UI-табов в UpgradesPanel. */
export type UpgradeCategory =
  | 'tap' // влияет на gain тапа
  | 'layer' // влияет на конкретный слой фразы
  | 'rhythm' // streak / дыхание
  | 'memory' // Atlas / Книга
  | 'guide' // нанимает / прокачивает Проводника (одноразовая)
  | 'building' // открывает / прокачивает постройку
  | 'unlock'; // открывает механику / фичу

/** Что игрок ВИДИТ когда покупает. Принцип #9 — обязательно. */
export interface UpgradeVisibleChange {
  /** Человекочитаемое описание для tooltip / лога: «Искра становится ярче». */
  readonly description: string;
  /**
   * CSS-переменная, которую UI устанавливает на корне приложения.
   * Пример: `--spark-glow-intensity`. Значение вычисляется из текущего
   * уровня через `cssValueFn` (если задан) или из effect.
   */
  readonly cssVariable?: string;
  readonly cssValueFn?: (level: number) => string;
  /** Имя флага, который React-компонент может подхватить. */
  readonly componentFlag?: string;
  /** ID частицного / SVG-эффекта чтобы wire через scheduler. */
  readonly particleEffect?: string;
  /** event_id из sound manifest, проигрывается one-shot при покупке. */
  readonly soundEffect?: string;
}

/** Что апгрейд делает с числами. Discriminated union для exhaustive checks. */
export type UpgradeEffect =
  | {
      readonly kind: 'multiplier';
      readonly target: 'tap' | 'passive' | 'streak';
      /** Опциональный layer-filter для layer-multiplier (тогда target = 'tap'). */
      readonly layer?: PhraseLayer;
      /** 0.05 = +5% за каждый уровень. */
      readonly multiplierPerLevel: number;
    }
  | {
      readonly kind: 'flat';
      readonly target: 'awarenesses_per_sec' | 'spirit_balls_per_min';
      readonly perLevel: number;
    }
  | {
      readonly kind: 'unlock';
      /** Произвольный id механики ('breath_cycle', 'tea_house' и т.д.). */
      readonly unlocks: string;
    }
  | {
      readonly kind: 'guide';
      readonly guideId: string;
    }
  | {
      readonly kind: 'building';
      readonly buildingId: string;
    }
  /**
   * JB 2026-05-25: апгрейды должны быть разнонаправленные, не только
   * множитель. Сокращает интервал между принятыми тапами на N ms за
   * уровень. Floor — `MIN_TAP_COOLDOWN_MS` (см. constants.ts), ниже не
   * упадёт даже при бесконечных уровнях.
   */
  | {
      readonly kind: 'cooldownReduction';
      /** Сколько ms снимает каждый уровень от BASE_TAP_COOLDOWN_MS. */
      readonly msPerLevel: number;
    }
  /**
   * Расширяет окно ритма (`RHYTHM_WINDOW_MS`). Игроку легче держать
   * streak — окно больше = больше времени между тапами. msPerLevel = 50
   * = на 5 уровнях окно вырастет на 250ms.
   */
  | {
      readonly kind: 'rhythmWindowExtension';
      readonly msPerLevel: number;
    }
  /**
   * Бонус к получаемым spirit_balls при кристаллизации. Множитель
   * (1 + perLevel × level) применяется к `spiritBallsToGain`.
   * 0.1 = +10% per level → к 5-му уровню кристаллизация даёт ×1.5.
   */
  | {
      readonly kind: 'crystallizationBonus';
      readonly perLevel: number;
    }
  /**
   * Auto-tap — каждые N миллисекунд игра имитирует тап от лица игрока
   * (используется applyTap c обычными бонусами). Doc 27 §1.
   * Лорно: Проводник касается Искры пока ты в покое.
   * `intervalMsAtLevel1` — стартовый интервал на ур.1.
   * `intervalMsPerLevel` — насколько уменьшается с уровнем (-).
   * Floor: 1000ms (1 авто-тап в секунду максимум).
   */
  | {
      readonly kind: 'autoTap';
      readonly intervalMsAtLevel1: number;
      readonly intervalMsPerLevel: number;
    }
  /**
   * Streak burst — каждый Nth тап в стрике даёт ×K бонус. Doc 27 §2.
   * `everyNth` — на каком тапе срабатывает (10 = каждый 10-й).
   * `baseMultiplier` — множитель на ур.1 (3 = ×3 награды).
   * `multiplierPerLevel` — добавляется к множителю с уровнем.
   * Применяется только если tapsInRhythmCurrent ≥ everyNth.
   */
  | {
      readonly kind: 'streakBurst';
      readonly everyNth: number;
      readonly baseMultiplier: number;
      readonly multiplierPerLevel: number;
    }
  /**
   * Passive awareness — N осознаний в секунду пока игра в фокусе или
   * через offline delta при возврате. Doc 27 §3.
   * Лорно: Искра дышит вместе с тобой.
   * `perSecAtLevel1` — стартовая скорость (1 = 1 ос./сек).
   * `perSecPerLevel` — растёт с уровнем.
   */
  | {
      readonly kind: 'passiveAwareness';
      readonly perSecAtLevel1: number;
      readonly perSecPerLevel: number;
    };

export interface Upgrade {
  /** Стабильный id — никогда не переименовывать. */
  readonly id: string;
  /** Короткое имя в духе Сай. */
  readonly name: string;
  /** Описание для карточки в магазине (короткое, тёплое, без негаций). */
  readonly description: string;
  readonly category: UpgradeCategory;
  /** Какая валюта тратится. */
  readonly currency: 'awarenesses' | 'spirit_balls';
  readonly baseCost: number;
  /**
   * Экспонента роста цены. 1.15 — мягкий рост для малых. 1.5 — большой
   * рост для больших.
   */
  readonly costGrowthRate: number;
  /** Максимальный уровень. Для guide/building/unlock = 1. */
  readonly maxLevel: number;
  /** Когда становится первый раз доступен. */
  readonly epoch: Epoch;
  /** Зависимость от другого апгрейда. */
  readonly requires?: {
    readonly upgradeId: string;
    readonly minLevel: number;
  };
  readonly effect: UpgradeEffect;
  readonly visibleChange: UpgradeVisibleChange;
}

/**
 * Карта `upgradeId → currentLevel` в game state. Map выбран для O(1)
 * lookup. Сериализуется в localStorage / БД как `[string, number][]`.
 */
export type PurchasedUpgrades = ReadonlyMap<string, number>;

// ---------------------------------------------------------------------------
// Cost / affordability
// ---------------------------------------------------------------------------

/**
 * Цена для покупки СЛЕДУЮЩЕГО уровня (current + 1).
 *
 * Округление до целого через `Math.floor` — никогда не наказываем игрока
 * за дробь, и в UI цена всегда читается ровным числом.
 */
export function computeUpgradeCost(upgrade: Upgrade, currentLevel: number): number {
  return Math.floor(upgrade.baseCost * Math.pow(upgrade.costGrowthRate, currentLevel));
}

/**
 * Сколько стоит купить N следующих уровней подряд. Полезно для
 * «купить макс» кнопки (когда добавим).
 */
export function computeBulkUpgradeCost(
  upgrade: Upgrade,
  currentLevel: number,
  count: number,
): number {
  let total = 0;
  for (let i = 0; i < count; i += 1) {
    total += computeUpgradeCost(upgrade, currentLevel + i);
  }
  return total;
}

/** Текущий уровень из state (0 = не куплен). */
export function getUpgradeLevel(purchased: PurchasedUpgrades, upgradeId: string): number {
  return purchased.get(upgradeId) ?? 0;
}

/**
 * Доступен ли апгрейд по гейтам:
 *   - текущая эпоха ≥ upgrade.epoch
 *   - зависимость (если есть) выполнена
 *   - текущий уровень < maxLevel
 *
 * Цена и валюта проверяются отдельно через `canAffordUpgrade`.
 */
export function isUpgradeUnlocked(
  upgrade: Upgrade,
  currentEpoch: Epoch,
  purchased: PurchasedUpgrades,
): boolean {
  if (currentEpoch < upgrade.epoch) return false;
  if (upgrade.requires !== undefined) {
    const reqLevel = getUpgradeLevel(purchased, upgrade.requires.upgradeId);
    if (reqLevel < upgrade.requires.minLevel) return false;
  }
  const myLevel = getUpgradeLevel(purchased, upgrade.id);
  if (myLevel >= upgrade.maxLevel) return false;
  return true;
}

export function canAffordUpgrade(
  upgrade: Upgrade,
  currentLevel: number,
  resources: ResourceState,
): boolean {
  const cost = computeUpgradeCost(upgrade, currentLevel);
  return resources[upgrade.currency] >= cost;
}

// ---------------------------------------------------------------------------
// Multiplier composition
// ---------------------------------------------------------------------------

/**
 * Из всех купленных multiplier-апгрейдов вычисляет суммарный бонус для
 * данного target / layer. Возвращает «бонус» в формате `composeGlobalMultiplier`
 * (0.5 = +50%), не множитель.
 *
 * Все купленные апгрейды по этому target/layer **складываются**:
 *   bonus = sum(multiplierPerLevel × level) для всех matching upgrades.
 * Это даёт линейный рост вместо мультипликативной экспоненты — мягче
 * для баланса в первых эпохах. Если поменяем на × — поменять и тут.
 */
export function applyUpgradesBonus(opts: {
  readonly purchased: PurchasedUpgrades;
  readonly catalog: readonly Upgrade[];
  readonly target: 'tap' | 'passive' | 'streak';
  readonly layer?: PhraseLayer;
}): number {
  let bonus = 0;
  for (const upgrade of opts.catalog) {
    if (upgrade.effect.kind !== 'multiplier') continue;
    if (upgrade.effect.target !== opts.target) continue;
    if (opts.layer !== undefined && upgrade.effect.layer !== opts.layer) continue;
    if (opts.layer === undefined && upgrade.effect.layer !== undefined) continue;
    const level = getUpgradeLevel(opts.purchased, upgrade.id);
    if (level <= 0) continue;
    bonus += upgrade.effect.multiplierPerLevel * level;
  }
  return bonus;
}

// ---------------------------------------------------------------------------
// Purchase reducer
// ---------------------------------------------------------------------------

export type PurchaseFailureReason =
  | 'locked' // epoch / dependency / maxLevel
  | 'insufficient_funds';

export interface PurchaseSuccess {
  readonly ok: true;
  /** New purchasedUpgrades map (with the level incremented). */
  readonly purchased: PurchasedUpgrades;
  /** New resources (cost subtracted). */
  readonly resources: ResourceState;
  /** Level AFTER purchase (1 for first buy, 2 for second, etc.). */
  readonly newLevel: number;
  /** What just got paid (for telemetry / animations). */
  readonly costPaid: number;
}

export interface PurchaseFailure {
  readonly ok: false;
  readonly reason: PurchaseFailureReason;
}

export type PurchaseResult = PurchaseSuccess | PurchaseFailure;

/**
 * Try to buy the next level of an upgrade. Pure — returns either a
 * new state slice or a failure reason. The store decides what to do
 * with the result (play sound, queue artifact, etc.).
 */
export function attemptPurchase(opts: {
  readonly upgrade: Upgrade;
  readonly currentEpoch: Epoch;
  readonly purchased: PurchasedUpgrades;
  readonly resources: ResourceState;
}): PurchaseResult {
  if (!isUpgradeUnlocked(opts.upgrade, opts.currentEpoch, opts.purchased)) {
    return { ok: false, reason: 'locked' };
  }
  const currentLevel = getUpgradeLevel(opts.purchased, opts.upgrade.id);
  const cost = computeUpgradeCost(opts.upgrade, currentLevel);
  if (opts.resources[opts.upgrade.currency] < cost) {
    return { ok: false, reason: 'insufficient_funds' };
  }
  const newLevel = currentLevel + 1;
  const nextPurchased = new Map(opts.purchased);
  nextPurchased.set(opts.upgrade.id, newLevel);
  const nextResources: ResourceState = {
    ...opts.resources,
    [opts.upgrade.currency]: opts.resources[opts.upgrade.currency] - cost,
  };
  return {
    ok: true,
    purchased: nextPurchased,
    resources: nextResources,
    newLevel,
    costPaid: cost,
  };
}

// ---------------------------------------------------------------------------
// Serialization helpers (for localStorage / DB)
// ---------------------------------------------------------------------------

export function purchasedUpgradesToArray(
  purchased: PurchasedUpgrades,
): readonly (readonly [string, number])[] {
  return Array.from(purchased.entries());
}

export function purchasedUpgradesFromArray(
  entries: readonly (readonly [string, number])[],
): PurchasedUpgrades {
  return new Map(entries);
}

/** Total levels across all upgrades. Used by `totalUpgradesAtLeast` trigger. */
export function getTotalUpgrades(purchased: PurchasedUpgrades): number {
  let total = 0;
  for (const level of purchased.values()) {
    total += level;
  }
  return total;
}

// ---------------------------------------------------------------------------
// Non-multiplier effect application
// ---------------------------------------------------------------------------

/**
 * Сумма ms которые надо вычесть из base cooldown. Применяется в `applyTap`
 * как `effectiveCooldown = max(MIN_TAP_COOLDOWN_MS, baseCooldown - reduction)`.
 */
export function computeCooldownReduction(
  purchased: PurchasedUpgrades,
  catalog: readonly Upgrade[],
): number {
  let total = 0;
  for (const upgrade of catalog) {
    if (upgrade.effect.kind !== 'cooldownReduction') continue;
    const level = getUpgradeLevel(purchased, upgrade.id);
    if (level <= 0) continue;
    total += upgrade.effect.msPerLevel * level;
  }
  return total;
}

/**
 * Сумма ms которые добавляются к base rhythm window. Применяется в
 * `applyTap` как `effectiveWindow = baseWindow + extension`.
 */
export function computeRhythmWindowExtension(
  purchased: PurchasedUpgrades,
  catalog: readonly Upgrade[],
): number {
  let total = 0;
  for (const upgrade of catalog) {
    if (upgrade.effect.kind !== 'rhythmWindowExtension') continue;
    const level = getUpgradeLevel(purchased, upgrade.id);
    if (level <= 0) continue;
    total += upgrade.effect.msPerLevel * level;
  }
  return total;
}

/**
 * Множитель бонуса к spirit_balls на кристаллизации. Возвращает
 * `1 + sum(perLevel × level)` — готовый к умножению на `spiritBallsToGain`.
 * При отсутствии апгрейдов = 1 (нет бонуса).
 */
export function computeCrystallizationBonusMultiplier(
  purchased: PurchasedUpgrades,
  catalog: readonly Upgrade[],
): number {
  let bonus = 0;
  for (const upgrade of catalog) {
    if (upgrade.effect.kind !== 'crystallizationBonus') continue;
    const level = getUpgradeLevel(purchased, upgrade.id);
    if (level <= 0) continue;
    bonus += upgrade.effect.perLevel * level;
  }
  return 1 + bonus;
}

/**
 * Минимальный интервал auto-tap'а в миллисекундах. Если несколько
 * апгрейдов с autoTap, берётся самый быстрый (Math.min). Floor = 1000ms.
 * 0 = нет auto-tap'а. Doc 27 §1.
 */
const AUTO_TAP_MIN_MS = 1_000;

export function computeAutoTapIntervalMs(
  purchased: PurchasedUpgrades,
  catalog: readonly Upgrade[],
): number {
  let best = 0; // 0 = нет авто-тапа
  for (const upgrade of catalog) {
    if (upgrade.effect.kind !== 'autoTap') continue;
    const level = getUpgradeLevel(purchased, upgrade.id);
    if (level <= 0) continue;
    const interval = Math.max(
      AUTO_TAP_MIN_MS,
      upgrade.effect.intervalMsAtLevel1 - upgrade.effect.intervalMsPerLevel * (level - 1),
    );
    if (best === 0 || interval < best) best = interval;
  }
  return best;
}

/**
 * Параметры streak burst — каждый Nth тап в стрике даёт ×K бонус.
 * Возвращает null если апгрейд не куплен. Doc 27 §2.
 *
 * Если куплено несколько streakBurst-апгрейдов, берётся первый с
 * наибольшим baseMultiplier × level (сильнейший эффект).
 */
export interface StreakBurstParams {
  readonly everyNth: number;
  readonly multiplier: number;
}

export function computeStreakBurst(
  purchased: PurchasedUpgrades,
  catalog: readonly Upgrade[],
): StreakBurstParams | null {
  let best: StreakBurstParams | null = null;
  let bestStrength = 0;
  for (const upgrade of catalog) {
    if (upgrade.effect.kind !== 'streakBurst') continue;
    const level = getUpgradeLevel(purchased, upgrade.id);
    if (level <= 0) continue;
    const multiplier =
      upgrade.effect.baseMultiplier + upgrade.effect.multiplierPerLevel * (level - 1);
    const strength = multiplier / upgrade.effect.everyNth;
    if (strength > bestStrength) {
      best = { everyNth: upgrade.effect.everyNth, multiplier };
      bestStrength = strength;
    }
  }
  return best;
}

/**
 * Сумма пассивных осознаний в секунду от всех passiveAwareness апгрейдов.
 * 0 = нет пассивного начисления. Doc 27 §3.
 */
export function computePassiveAwarenessPerSec(
  purchased: PurchasedUpgrades,
  catalog: readonly Upgrade[],
): number {
  let total = 0;
  for (const upgrade of catalog) {
    if (upgrade.effect.kind !== 'passiveAwareness') continue;
    const level = getUpgradeLevel(purchased, upgrade.id);
    if (level <= 0) continue;
    total += upgrade.effect.perSecAtLevel1 + upgrade.effect.perSecPerLevel * (level - 1);
  }
  return total;
}

// ---------------------------------------------------------------------------
// Effect description — human-readable math for UI
// ---------------------------------------------------------------------------

/**
 * Summary of what an upgrade does at the current level and what the next
 * level will add. Designed for direct display on a UpgradeCard so the
 * player sees the math, not just "Искра становится ярче".
 *
 * Example for u001_bright_spark at level 4:
 *   currentLabel:  "сейчас даёт +20% к тапу"
 *   nextLabel:     "следующий: +25% (+5%)"
 *
 * Returns null when the upgrade has nothing numeric to show (e.g. a
 * guide whose effect is presence, not math).
 */
export interface UpgradeEffectSummary {
  /** What the upgrade currently does (omitted at level 0). */
  readonly currentLabel: string | null;
  /** What the next purchase will change. */
  readonly nextLabel: string | null;
}

export function describeUpgradeEffect(
  upgrade: Upgrade,
  currentLevel: number,
): UpgradeEffectSummary {
  const eff = upgrade.effect;
  const atMax = currentLevel >= upgrade.maxLevel;

  if (eff.kind === 'multiplier') {
    const subject = describeMultiplierSubject(eff.target);
    const currentPct = currentLevel * eff.multiplierPerLevel * 100;
    const nextPct = (currentLevel + 1) * eff.multiplierPerLevel * 100;
    const deltaPct = eff.multiplierPerLevel * 100;
    return {
      currentLabel:
        currentLevel === 0 ? null : `${subject} на ${formatPct(currentPct)} больше осознаний`,
      nextLabel: atMax
        ? null
        : `после покупки → +${formatPct(nextPct)} (ещё +${formatPct(deltaPct)})`,
    };
  }

  if (eff.kind === 'flat') {
    const targetLabel = describeFlatTarget(eff.target);
    const current = currentLevel * eff.perLevel;
    const next = (currentLevel + 1) * eff.perLevel;
    return {
      currentLabel:
        currentLevel === 0 ? null : `сейчас приносит +${formatNumeric(current)} ${targetLabel}`,
      nextLabel: atMax
        ? null
        : `после покупки → +${formatNumeric(next)} ${targetLabel} (ещё +${formatNumeric(eff.perLevel)})`,
    };
  }

  if (eff.kind === 'cooldownReduction') {
    const currentMs = currentLevel * eff.msPerLevel;
    const nextMs = (currentLevel + 1) * eff.msPerLevel;
    return {
      currentLabel:
        currentLevel === 0 ? null : `Искра готова на ${formatNumeric(currentMs)} ms быстрее`,
      nextLabel: atMax
        ? null
        : `после покупки → −${formatNumeric(nextMs)} ms (ещё −${formatNumeric(eff.msPerLevel)} ms)`,
    };
  }

  if (eff.kind === 'rhythmWindowExtension') {
    const currentMs = currentLevel * eff.msPerLevel;
    const nextMs = (currentLevel + 1) * eff.msPerLevel;
    return {
      currentLabel:
        currentLevel === 0
          ? null
          : `окно ритма шире на ${formatNumeric(currentMs)} ms — легче держать стрик`,
      nextLabel: atMax
        ? null
        : `после покупки → +${formatNumeric(nextMs)} ms (ещё +${formatNumeric(eff.msPerLevel)} ms)`,
    };
  }

  if (eff.kind === 'crystallizationBonus') {
    const currentPct = currentLevel * eff.perLevel * 100;
    const nextPct = (currentLevel + 1) * eff.perLevel * 100;
    const deltaPct = eff.perLevel * 100;
    return {
      currentLabel:
        currentLevel === 0 ? null : `кристаллизация даёт +${formatPct(currentPct)} духобаллов`,
      nextLabel: atMax
        ? null
        : `после покупки → +${formatPct(nextPct)} (ещё +${formatPct(deltaPct)})`,
    };
  }

  if (eff.kind === 'guide') {
    if (currentLevel >= 1) {
      return { currentLabel: 'Проводник пришёл в Рощу', nextLabel: null };
    }
    return { currentLabel: null, nextLabel: 'призовёт первого Проводника' };
  }

  if (eff.kind === 'building') {
    if (currentLevel >= 1) {
      return { currentLabel: 'постройка стоит', nextLabel: null };
    }
    return { currentLabel: null, nextLabel: 'построит здание в Роще' };
  }

  if (eff.kind === 'autoTap') {
    if (currentLevel === 0) {
      const sec = (eff.intervalMsAtLevel1 / 1000).toFixed(0);
      return { currentLabel: null, nextLabel: `Проводник касается Искры каждые ${sec} сек` };
    }
    const currentInterval = Math.max(
      1000,
      eff.intervalMsAtLevel1 - eff.intervalMsPerLevel * (currentLevel - 1),
    );
    const nextInterval = Math.max(
      1000,
      eff.intervalMsAtLevel1 - eff.intervalMsPerLevel * currentLevel,
    );
    return {
      currentLabel: `авто-тап каждые ${(currentInterval / 1000).toFixed(1)} сек`,
      nextLabel: atMax ? null : `после покупки → каждые ${(nextInterval / 1000).toFixed(1)} сек`,
    };
  }

  if (eff.kind === 'streakBurst') {
    if (currentLevel === 0) {
      return {
        currentLabel: null,
        nextLabel: `каждый ${eff.everyNth.toString()}-й тап в стрике даёт ×${eff.baseMultiplier.toFixed(1)}`,
      };
    }
    const currentMult = eff.baseMultiplier + eff.multiplierPerLevel * (currentLevel - 1);
    const nextMult = eff.baseMultiplier + eff.multiplierPerLevel * currentLevel;
    return {
      currentLabel: `каждый ${eff.everyNth.toString()}-й тап в стрике × ${currentMult.toFixed(1)}`,
      nextLabel: atMax ? null : `после покупки → × ${nextMult.toFixed(1)}`,
    };
  }

  if (eff.kind === 'passiveAwareness') {
    if (currentLevel === 0) {
      return {
        currentLabel: null,
        nextLabel: `Искра приносит ${eff.perSecAtLevel1.toString()} ос./сек пассивно`,
      };
    }
    const currentRate = eff.perSecAtLevel1 + eff.perSecPerLevel * (currentLevel - 1);
    const nextRate = eff.perSecAtLevel1 + eff.perSecPerLevel * currentLevel;
    return {
      currentLabel: `пассив: +${currentRate.toFixed(1)} ос./сек`,
      nextLabel: atMax ? null : `после покупки → +${nextRate.toFixed(1)} ос./сек`,
    };
  }

  // eff.kind === 'unlock' — последняя ветка из дискриминированного union.
  if (currentLevel >= 1) {
    return { currentLabel: 'механика открыта', nextLabel: null };
  }
  return { currentLabel: null, nextLabel: 'откроет новую механику' };
}

/** «каждый тап даёт» / «пассив даёт» / «в стрике каждый тап даёт». */
function describeMultiplierSubject(target: 'tap' | 'passive' | 'streak'): string {
  switch (target) {
    case 'tap':
      return 'каждый тап даёт';
    case 'passive':
      return 'пассивный поток даёт';
    case 'streak':
      return 'в стрике каждый тап даёт';
  }
}

function describeFlatTarget(target: 'awarenesses_per_sec' | 'spirit_balls_per_min'): string {
  switch (target) {
    case 'awarenesses_per_sec':
      return 'осознаний/сек';
    case 'spirit_balls_per_min':
      return 'духобаллов/мин';
  }
}

/** Format percentage with one decimal if needed, otherwise integer. */
function formatPct(value: number): string {
  if (Math.abs(value - Math.round(value)) < 0.05) return `${Math.round(value).toString()}%`;
  return `${value.toFixed(1)}%`;
}

/** Format flat numeric — integer if whole, one decimal otherwise. */
function formatNumeric(value: number): string {
  if (Math.abs(value - Math.round(value)) < 0.05) return Math.round(value).toString();
  return value.toFixed(1);
}
